/**
 * One assessment mode (QUIZ or QUESTIONNAIRE) sub-row of a product type block.
 *
 * Single-subject clients (personal, company, POA/group-with-representative)
 * render one actionable line. A group without a representative (QL-505)
 * renders the group's verdict for the mode plus a count, and — expanded —
 * one line per member, each fed with that member's own state through the
 * SAME action renderers a single subject's line uses (`renderQuizAction` /
 * `renderQuestionnaireAction`), so a member's line is never a re-implementation.
 */

import { memberLineAttribution } from "@repo/shared";
import {
  type AnsweringPartyRecord,
  type AssessmentModeStatus,
  classifyOutcomeStatus,
  classifyQuizStatus,
  combineModeStatuses,
  getQuizAttemptAvailability,
  type MemberRepresentative,
  type ProductAssessmentExperienceResult,
  type ProductAssessmentOutcome,
  type ProductAssessmentQuizState,
  type ProductAssessmentVerdict,
  type ProductTypeAssessmentMode,
  type ProductTypeAssessmentPublic,
  Translated,
} from "@repo/shared/types";
import { CheckCircle2, ChevronDown, ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useT } from "@/lib/i18n/use-t";
import { MemberLine } from "./member-line";
import { T } from "./mode-row.i18n";

export type ExternalOutcome = {
  passed: boolean;
  source: string;
  completedAt: string;
  reference?: string | null;
};

/**
 * QL-660 — whether THIS mode's line has an answer yet (picks "answered by" over
 * "answers through"). `quizState` is per product type and `hasAnswers` holds the
 * questionnaire's answers, so each counts only for its own mode: a quiz attempt
 * must not mark the questionnaire row of the same type answered.
 */
export function lineAnswered(line: AssessmentLine, mode: ProductTypeAssessmentMode): boolean {
  if (line.external) return true;
  return mode === "QUIZ"
    ? (line.quizState?.attempts ?? []).some((a) => a.completedAt !== null)
    : line.hasAnswers;
}

/** One line's live state — a single subject's own, or one member's own. */
export type AssessmentLine = {
  quizState: ProductAssessmentQuizState | null;
  questionnaireAnswers: Record<string, string>;
  external: ExternalOutcome | null;
  /**
   * QL-505 (G3 follow-up) — this line's own stored QUESTIONNAIRE outcome
   * (status/passed/completedAt), read straight from calculatedData. QUIZ
   * status stays live-computed from `quizState` + attempt availability (see
   * `resolveSubjectVerdict` above) — this is consulted only for the
   * questionnaire action line's status text. Undefined/null while nothing has
   * been computed yet for this subject/member.
   */
  questionnaireOutcome?: ProductAssessmentOutcome | null;
  /** QL-660 — the frozen record of the answer that COUNTS for this line (server outcome), if any. */
  answeredBy?: AnsweringPartyRecord | null;
  /** Can THIS line act right now (not read-only for this subject/member). */
  actionable: boolean;
  /** This line's own quiz-start request is in flight. */
  isStarting: boolean;
  /** Any quiz-start request on this widget is in flight — every start button disables together. */
  anyStarting: boolean;
  hasAnswers: boolean;
  onStartQuiz: () => void;
  onOpenQuestionnaire: () => void;
};

export type ModeGroupState = {
  status: AssessmentModeStatus;
  /** QL-653 — a member session: the viewer's own result for this mode; shown instead of the aggregate. */
  ownStatus?: AssessmentModeStatus;
  memberSummary?: { passed: number; total: number };
  expanded: boolean;
  onToggleExpand: () => void;
  members: Array<
    AssessmentLine & {
      subjectId: string;
      name: string;
      /** QL-660 — who answers for this member (their own POA/guardian), if anyone. */
      memberRepresentative?: MemberRepresentative | null;
    }
  >;
};

type ModeRowProps = {
  /** QL-654 — 2+ representatives: the member toggle says "representatives". */
  representativeList: boolean;
  mode: ProductTypeAssessmentMode;
  assessment: ProductTypeAssessmentPublic;
  formatDate: (iso: string) => string;
  /** Present for every non-group client (single answering subject). */
  single?: AssessmentLine;
  /** Present only for a group without a representative. */
  group?: ModeGroupState;
};

// --- Pure lookups, reused by the block header (verdict badge) and the widget
// (top-level single-subject verdict + "not suitable but in use" check). ---

export function findOutcome(
  experiences: ProductAssessmentExperienceResult[] | undefined,
  typeId: string,
  mode: ProductTypeAssessmentMode,
): ProductAssessmentOutcome | undefined {
  return experiences
    ?.find((e) => e.productTypeId === typeId)
    ?.assessments?.find((o) => o.mode === mode);
}

export function externalFor(
  experiences: ProductAssessmentExperienceResult[] | undefined,
  typeId: string,
  mode: ProductTypeAssessmentMode,
): ExternalOutcome | null {
  const outcome = findOutcome(experiences, typeId, mode);
  if (!outcome || outcome.resultSource !== "external" || !outcome.external) return null;
  return { passed: outcome.passed, ...outcome.external };
}

/**
 * Live category verdict for ONE answering subject (single client, or one
 * member) — the server-stored verdict with the QUIZ mode overlaid by current
 * time (cooldown ages independently of the last save). Legacy rows without a
 * stored verdict fall back to a record-based classification.
 */
export function resolveSubjectVerdict(
  configured: ProductTypeAssessmentPublic[],
  experiences: ProductAssessmentExperienceResult[] | undefined,
  typeId: string,
  quizStates: Record<string, ProductAssessmentQuizState>,
): ProductAssessmentVerdict {
  if (configured.length === 0) return "incomplete";
  const entry = experiences?.find((e) => e.productTypeId === typeId);
  const storedStatus = (mode: ProductTypeAssessmentMode): AssessmentModeStatus => {
    const outcome = findOutcome(experiences, typeId, mode);
    if (!outcome) return "incomplete";
    return outcome.status ?? classifyOutcomeStatus(outcome);
  };
  const hasQuiz = configured.some((a) => a.mode === "QUIZ");
  if (!hasQuiz && entry?.verdict) return entry.verdict;
  return combineModeStatuses(
    configured.map((assessment) => {
      if (assessment.mode !== "QUIZ") return storedStatus(assessment.mode);
      if (externalFor(experiences, typeId, "QUIZ")) return storedStatus("QUIZ");
      const state = quizStates[typeId];
      const availability = getQuizAttemptAvailability(
        state,
        assessment.maxAttempts,
        assessment.attemptResetDays,
        new Date(),
      );
      return classifyQuizStatus(state, availability);
    }),
  );
}

/**
 * "Recorded externally · Acme Onboarding · 12 Mar 2026" — shown in place of
 * this line's own result, with the action left in place: a recorded result
 * never blocks taking the assessment here, and doing so supersedes it.
 */
function renderExternalLine(
  external: ExternalOutcome,
  t: Translated<typeof T>,
  formatDate: (iso: string) => string,
): ReactNode {
  return (
    <span className="flex flex-wrap items-center gap-1.5 text-sm">
      <span className={external.passed ? "text-success" : "text-destructive"}>
        {external.passed ? t.externalPassed : t.externalFailed}
      </span>
      <span className="text-muted-foreground">
        {t.recordedExternally}
        {external.source ? ` · ${external.source}` : ""}
        {external.completedAt ? ` · ${formatDate(external.completedAt)}` : ""}
      </span>
    </span>
  );
}

/**
 * QL-505 (G3 follow-up) — the QUESTIONNAIRE line's own status, mirroring the
 * three states a quiz line shows: Passed (with date) / Not passed / Not
 * answered. Read straight from the stored outcome's `status` — a
 * questionnaire has no cooldown/attempt-availability clock to overlay, unlike
 * QUIZ, so the stored status is authoritative.
 */
function renderQuestionnaireStatus(
  outcome: ProductAssessmentOutcome | null | undefined,
  t: Translated<typeof T>,
  formatDate: (iso: string) => string,
): ReactNode {
  if (outcome?.status === "passed") {
    return (
      <span className="flex flex-wrap items-center gap-1.5 text-sm text-success">
        <CheckCircle2 className="h-4 w-4" />
        {t.questionnairePassed}
        {outcome.completedAt && (
          <span className="hidden text-muted-foreground sm:inline">
            {formatDate(outcome.completedAt)}
          </span>
        )}
      </span>
    );
  }
  if (outcome?.status === "failed") {
    return <span className="text-sm text-destructive">{t.questionnaireNotPassed}</span>;
  }
  return <span className="text-sm text-muted-foreground">{t.notAnswered}</span>;
}

function renderQuestionnaireAction(
  line: AssessmentLine,
  t: Translated<typeof T>,
  formatDate: (iso: string) => string,
): ReactNode {
  if (line.external) {
    return (
      <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-2">
        {renderExternalLine(line.external, t, formatDate)}
        {line.actionable && (
          <Button size="sm" variant="outline" onClick={line.onOpenQuestionnaire}>
            {t.retakeHere}
          </Button>
        )}
      </div>
    );
  }
  // QL-505 (G3 follow-up) — the status text is shown next to the action
  // button whenever there is something to report (answers already given);
  // when nothing has been answered yet the button's own label ("Answer
  // questions") already says so, so no separate "Not answered" text is added.
  const statusNode = line.hasAnswers
    ? renderQuestionnaireStatus(line.questionnaireOutcome, t, formatDate)
    : null;
  if (!line.actionable) {
    return line.hasAnswers ? (
      <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-2">
        {statusNode}
        <Button size="sm" variant="outline" onClick={line.onOpenQuestionnaire}>
          {t.viewAnswers}
        </Button>
      </div>
    ) : (
      <span className="text-sm text-muted-foreground">{t.notAnswered}</span>
    );
  }
  return (
    <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-2">
      {statusNode}
      <Button
        size="sm"
        variant={line.hasAnswers ? "outline" : "default"}
        onClick={line.onOpenQuestionnaire}
      >
        {line.hasAnswers ? t.editAnswers : t.answerQuestions}
      </Button>
    </div>
  );
}

function renderQuizAction(
  assessment: ProductTypeAssessmentPublic,
  line: AssessmentLine,
  t: Translated<typeof T>,
  formatDate: (iso: string) => string,
): ReactNode {
  const availability = getQuizAttemptAvailability(
    line.quizState,
    assessment.maxAttempts,
    assessment.attemptResetDays,
    new Date(),
  );
  const passed = line.quizState?.passed ?? false;
  const hasFailedAttempt = !passed && (line.quizState?.attemptsCount ?? 0) > 0;

  if (line.external) {
    // The record consumed no attempts, so `availability.canAttempt` is still
    // true here — starting a quiz supersedes it, which is the point.
    return (
      <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-2">
        {renderExternalLine(line.external, t, formatDate)}
        {line.actionable && availability.canAttempt && (
          <Button
            size="sm"
            variant="outline"
            onClick={line.onStartQuiz}
            disabled={line.anyStarting}
          >
            {line.isStarting ? t.starting : t.retakeHere}
          </Button>
        )}
      </div>
    );
  }
  if (passed || availability.locked) {
    return (
      <span className="flex flex-wrap items-center gap-1.5 text-sm text-success">
        <CheckCircle2 className="h-4 w-4" />
        {t.quizPassed}
        {line.quizState?.lastAttemptAt && (
          <span className="hidden text-muted-foreground sm:inline">
            {formatDate(line.quizState.lastAttemptAt)}
          </span>
        )}
      </span>
    );
  }
  if (!line.actionable) {
    return (
      <span className="text-sm text-muted-foreground">
        {hasFailedAttempt ? t.quizNotPassed : t.quizNotCompleted}
      </span>
    );
  }
  if (availability.canAttempt) {
    const label = hasFailedAttempt
      ? availability.remaining != null
        ? t.retryWithRemaining(availability.remaining)
        : t.retry
      : t.startQuiz;
    return (
      <Button
        size="sm"
        variant={hasFailedAttempt ? "outline" : "default"}
        onClick={line.onStartQuiz}
        disabled={line.anyStarting}
      >
        {line.isStarting ? t.starting : label}
      </Button>
    );
  }
  if (availability.cooldownUntil) {
    return (
      <Button size="sm" variant="outline" disabled>
        {t.retryAvailableOn(formatDate(availability.cooldownUntil))}
      </Button>
    );
  }
  return <span className="text-sm text-muted-foreground">{t.noAttemptsRemaining}</span>;
}

/**
 * Renders one line's action/state for its mode. Exported so
 * `ProductTypeBlock` can render the SAME action for a product type with
 * exactly one assessment mode, folded into its header instead of a separate
 * `ModeRow` (QL-505 G3 follow-up) — a single subject's line is never a
 * re-implementation of this.
 */
function renderAction(
  mode: ProductTypeAssessmentMode,
  assessment: ProductTypeAssessmentPublic,
  line: AssessmentLine,
  t: Translated<typeof T>,
  formatDate: (iso: string) => string,
): ReactNode {
  return mode === "QUESTIONNAIRE"
    ? renderQuestionnaireAction(line, t, formatDate)
    : renderQuizAction(assessment, line, t, formatDate);
}

/** `renderAction` as a component, for the block header and its member lines. */
export function ModeAction({
  mode,
  assessment,
  line,
  formatDate,
}: {
  mode: ProductTypeAssessmentMode;
  assessment: ProductTypeAssessmentPublic;
  line: AssessmentLine;
  formatDate: (iso: string) => string;
}) {
  const t = useT(T);
  return renderAction(mode, assessment, line, t, formatDate);
}

function statusBadge(status: AssessmentModeStatus, t: Translated<typeof T>) {
  if (status === "passed")
    return (
      <Badge variant="success" className="whitespace-nowrap">
        {t.verdictPassed}
      </Badge>
    );
  if (status === "failed")
    return (
      <Badge variant="destructive" className="whitespace-nowrap">
        {t.verdictFailed}
      </Badge>
    );
  return (
    <Badge variant="secondary" className="whitespace-nowrap">
      {t.verdictIncomplete}
    </Badge>
  );
}

export function ModeRow({
  mode,
  assessment,
  representativeList,
  formatDate,
  single,
  group,
}: ModeRowProps) {
  const t = useT(T);
  // QL-654 — 2+ representatives answer in the same list as a group's members.
  const expandLabel = representativeList ? t.expandRepresentatives : t.expandMembers;
  const collapseLabel = representativeList ? t.collapseRepresentatives : t.collapseMembers;
  const label = mode === "QUIZ" ? t.quizLabel : t.questionnaireLabel;

  if (single) {
    return (
      <div className="flex min-h-9 flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <Label className="font-normal">{label}</Label>
        <div className="ml-auto">{renderAction(mode, assessment, single, t, formatDate)}</div>
      </div>
    );
  }

  if (!group) return null;

  const countText = group.memberSummary
    ? t.memberCountPassed(group.memberSummary.passed, group.memberSummary.total)
    : null;

  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={group.onToggleExpand}
        aria-expanded={group.expanded}
        aria-label={`${group.expanded ? collapseLabel : expandLabel}: ${label}`}
        className="flex min-h-9 w-full items-center justify-between gap-3 text-left"
      >
        <span className="flex min-w-0 items-center gap-2">
          {group.expanded ? (
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          )}
          <Label className="truncate font-normal cursor-pointer">{label}</Label>
        </span>
        <span className="flex shrink-0 items-center gap-2 whitespace-nowrap">
          {countText && <span className="text-sm text-muted-foreground">{countText}</span>}
          {statusBadge(group.ownStatus ?? group.status, t)}
        </span>
      </button>
      {group.expanded && (
        <div className="space-y-1 pl-6">
          {group.members.map((member) => (
            <MemberLine
              key={member.subjectId}
              subjectId={member.subjectId}
              name={member.name}
              {...memberLineAttribution({
                subjectId: member.subjectId,
                liveRepresentative: member.memberRepresentative,
                answered: lineAnswered(member, mode),
                answeredBy: member.answeredBy,
                advisorOrExternal: !!member.external,
              })}
              language={t.locale}
            >
              {renderAction(mode, assessment, member, t, formatDate)}
            </MemberLine>
          ))}
        </div>
      )}
    </div>
  );
}
